/* 效能採集層。

   設計原則
   1. 關閉時成本趨近於零 —— 所有量測點都先看 `enabled`，關掉時只是一次布林判斷。
   2. 不看平均值就下結論 —— 掉幀是尖峰造成的，平均值會把它藏起來。
      所以保留最近 N 幀的環形緩衝，算 p50 / p95 / max。
   3. 分離「模擬」與「渲染」—— 這兩者的最佳化手段完全不同（演算法 vs draw call），
      只知道總幀時間沒辦法決定要修哪邊。

   為什麼自己寫而不用瀏覽器 profiler：profiler 看得到函式層級的火焰圖，但看不到
   「這一幀場上有幾隻敵人、跑了幾個模擬步」。效能問題的歸因需要遊戲側的脈絡。 */

const RING = 180;   // 約三秒（60fps），足以看出尖峰又不會吃記憶體

type Channel = { buf: Float64Array; i: number; n: number };

function channel(): Channel {
  return { buf: new Float64Array(RING), i: 0, n: 0 };
}

function push(c: Channel, v: number) {
  c.buf[c.i] = v;
  c.i = (c.i + 1) % RING;
  if (c.n < RING) c.n++;
}

/** 回傳 [p50, p95, max]。n 小的時候直接排序就好，RING 只有 180。 */
function stats(c: Channel): [number, number, number] {
  if (c.n === 0) return [0, 0, 0];
  const a = Array.prototype.slice.call(c.buf, 0, c.n) as number[];
  a.sort((x, y) => x - y);
  return [a[(a.length * 0.5) | 0], a[(a.length * 0.95) | 0], a[a.length - 1]];
}

const channels: Record<string, Channel> = {
  frame: channel(),    // 一整幀的牆鐘時間
  sim: channel(),      // 該幀所有模擬步的總時間
  render: channel(),   // renderer.render 的時間
  steps: channel(),    // 該幀跑了幾個模擬步
};

/** 計數類指標：每幀覆寫，不做統計。 */
export const counters = {
  drawCalls: 0, triangles: 0, geometries: 0, textures: 0,
  enemies: 0, bullets: 0, ebullets: 0, debris: 0, orbs: 0,
};

const now = () => performance.now();
let marks: Record<string, number> = {};

export const perf = {
  /* 計時常態開啟：成本只有每幀幾次 performance.now 與環形緩衝寫入，
     而動態解析度需要它一直有數據可以看。 */
  enabled: true,
  /* 計數類（draw call、場上實體數）比較貴 —— countAlive 是 O(n) ——
     只在效能面板開啟時才收。 */
  detailed: false,

  /** 開始一段計時。關閉時是一次布林判斷。 */
  begin(label: string) {
    if (!perf.enabled) return;
    marks[label] = now();
  },

  /** 結束一段計時並記錄。 */
  end(label: string) {
    if (!perf.enabled) return;
    const t = marks[label];
    if (t === undefined) return;
    const c = channels[label];
    if (c) push(c, now() - t);
  },

  /** 直接記一個值（例如該幀的模擬步數）。 */
  value(label: string, v: number) {
    if (!perf.enabled) return;
    const c = channels[label];
    if (c) push(c, v);
  },

  /** 每幀結束時呼叫，把 Three.js 與場上實體的計數抄過來。 */
  collect(renderer: any, counts: Partial<typeof counters>) {
    if (!perf.detailed) return;
    if (renderer && renderer.info) {
      counters.drawCalls = renderer.info.render.calls;
      counters.triangles = renderer.info.render.triangles;
      counters.geometries = renderer.info.memory.geometries;
      counters.textures = renderer.info.memory.textures;
    }
    for (const k in counts) (counters as any)[k] = (counts as any)[k];
  },

  /** 給 overlay 與自動化量測用的快照。 */
  snapshot() {
    const [fp50, fp95, fmax] = stats(channels.frame);
    const [sp50, , smax] = stats(channels.sim);
    const [rp50, , rmax] = stats(channels.render);
    const [stp50, , stmax] = stats(channels.steps);
    return {
      samples: channels.frame.n,
      frame: { p50: fp50, p95: fp95, max: fmax, fps: fp50 > 0 ? 1000 / fp50 : 0 },
      sim: { p50: sp50, max: smax },
      render: { p50: rp50, max: rmax },
      steps: { p50: stp50, max: stmax },
      counters: { ...counters },
    };
  },

  reset() {
    for (const k in channels) { channels[k].i = 0; channels[k].n = 0; }
    marks = {};
  },
};
